import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FlexRow,
  FlexBaseCol,
  P,
  H6,
  Dropdown,
  Icon,
  Styles,
  StyledToast
} from '@contently/toolkit';
import styled from '@emotion/styled';
import { ComposedChart, ResponsiveContainer, Line, Bar, YAxis, XAxis, LabelList } from 'recharts';
import { CSVLink } from 'react-csv';
import { ToastContainer, toast } from 'react-toastify';
import useFilter from './useFilter';
import timelineHelper from './timelineHelper';

const FilterBorder = styled(Dropdown)`
  border-radius: 3px;
  border: 1px solid #d4d4d4;
  box-shadow: none;
  margin-right: 18px;
`;

const PaddedFilterText = styled(P)`
  padding-right: 0.5rem;
`;

const CsvDownloadBox = styled('div')`
  border: 1px solid #d4d4d4;
  padding: 2px 0;
  margin-top: 1px;
  position: absolute;
  width: 130px;
  z-index: 1;
  &:hover {
    background-color: ${Styles.colors.blue.secondary};
    cursor: pointer;
  }
  a {
    text-decoration: none;
  }
`;

const CsvDownloadText = styled(P)`
  padding: 0.5rem;
  font-weight: bold;
  font-size: 12px;
`;

const BarLegend = styled('div')`
  background-color: ${Styles.colors.blue.secondary};
  width: 2rem;
  margin: 5px;
  padding: 0.75rem;
`;

const LineLegend = styled('div')`
  background-color: ${Styles.colors.blue.primaryDark};
  width: 2rem;
  height: 2px;
  margin: 5px;
  padding: 2px;
`;

const SpacedRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MarginP = styled(P)`
  margin-bottom: 0px !important;
  margin-right: 0.5rem;
`;

const CenteredFlexRow = styled(FlexRow)`
  align-items: center;
`;

const timelines = ['All Time', 'Last 12 Months', 'Last 6 Months', 'Last 3 Months'];

function MonthlyChartWrapper({ data, pubInfo, csvData }) {
  const [timelineState, setTimelineState] = useState(
    localStorage.getItem('contentValueChartTimeline') || 'All Time'
  );

  const [csvVisibility, setCsvVisibility] = useState(false);

  const dropdownActions = useFilter(
    timelineState,
    setTimelineState,
    'contentValueChartTimeline',
    timelines
  );

  const timelineData = timelineHelper(data, timelineState);
  const csvTimelineData = timelineHelper(csvData, timelineState);

  const TriggerButton = () => (
    <FlexRow margin="xsmall">
      <PaddedFilterText>{timelineState}</PaddedFilterText>
      <Icon type="calendar" color={Styles.colors.blue.secondaryDark} />
    </FlexRow>
  );

  const renderCustomizedLabel = props => {
    // eslint-disable-next-line react/prop-types
    const { x, y, width, value } = props;

    if (!value) return null;

    return (
      <text x={x + width / 2} y={y} dy={14} textAnchor="middle">
        {value}
      </text>
    );
  };

  const CsvDownloadButton = () => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getMonth() +
      1}–${currentDate.getDate()}–${currentDate.getFullYear()}`;
    const filename = `${pubInfo.pubName} #${pubInfo.pubId} Content Value Dashboard — ${formattedDate}`;
    const notificationOptions = { type: 'success', autoClose: 2000 };

    const csvOnClick = () => {
      toast('Your csv is processing...', notificationOptions);
      setCsvVisibility(!csvVisibility);
    };

    return (
      <CsvDownloadBox>
        <CSVLink data={csvTimelineData} filename={`${filename}.csv`} onClick={() => csvOnClick()}>
          <CsvDownloadText>Download
            CSV</CsvDownloadText>
        </CSVLink>
      </CsvDownloadBox>
    );
  };

  return (
    <Fragment>
      <StyledToast>
        <ToastContainer />
      </StyledToast>
      <SpacedRow>
        <H6>SEO TRAFFIC VALUE HISTORY</H6>
        <div>
          <FilterBorder links={dropdownActions} Trigger={TriggerButton} />
          <Icon
            type="ellipsis-v"
            color={Styles.colors.blue.secondaryDark}
            style={{ cursor: 'pointer' }}
            onClick={() => setCsvVisibility(!csvVisibility)}
          />
          {csvVisibility ? <CsvDownloadButton /> : null}
        </div>
      </SpacedRow>
      <CenteredFlexRow>
        <LineLegend />
        <MarginP>SEO Traffic Value</MarginP>
        <BarLegend />
        <P>Published Stories</P>
      </CenteredFlexRow>
      <FlexRow>
        <FlexBaseCol>
          <ResponsiveContainer width="99%" height={400}>
            <ComposedChart
              data={timelineData}
              margin={{
                top: 20,
                bottom: 20,
                right: 20,
                left: 20
              }}
            >
              <XAxis dataKey="month" />
              <YAxis
                yAxisId="left"
                orientation="left"
                domain={[dataMin => dataMin * 0.95, 'auto']}
                padding={!data.length < 1 && { top: 160 }}
                hide
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={['auto', dataMax => dataMax * 1.2]}
                padding={!data.length < 1 && { bottom: 180 }}
                hide
              />
              <Bar
                yAxisId="left"
                dataKey="barData"
                fill={Styles.colors.blue.secondary}
                label={renderCustomizedLabel}
              />
              <Line
                yAxisId="right"
                dataKey="lineData"
                type="natural"
                stroke={Styles.colors.blue.primaryDark}
                strokeWidth={2}
              >
                <LabelList dataKey="lineDataLabel" position="top" />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </FlexBaseCol>
      </FlexRow>
    </Fragment>
  );
}

export default MonthlyChartWrapper;

MonthlyChartWrapper.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      lineData: PropTypes.number,
      lineDataLabel: PropTypes.string,
      barData: PropTypes.number
    })
  ).isRequired,
  pubInfo: PropTypes.shape({
    pubId: PropTypes.number,
    pubName: PropTypes.string
  }).isRequired,
  csvData: PropTypes.arrayOf(
    PropTypes.shape({
      'Month-Year': PropTypes.string,
      'Total SEO Traffic Value: All Regions': PropTypes.string,
      'Total SEO Traffic Value: Canada': PropTypes.string,
      'Total SEO Traffic Value: US': PropTypes.string,
      'Total Stories Published': PropTypes.number
    })
  ).isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/value/monthly_chart_wrapper.jsx